import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { EarlyDelivery, apiErrorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertOctagon, Search, CheckCircle2, XCircle, Eye, User, Bike, Clock,
  ListTodo, MessageSquare,
} from "lucide-react";
import {
  StatCard, EmptyState,
} from "../components/design-system";
import { cardCls, labelCls } from "../components/design-system/tokens";
import { TASK_TYPE_LABELS } from "../lib/constants";
import { fmtDateTime } from "../lib/auditFormat";

const STATUS_TABS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

/**
 * Accounts → Early Delivery Approvals queue.
 *
 * Built on the OneDesk Design System (StatCard, design tokens) — replaces
 * the heavier shadcn-table look used elsewhere in Accounts. Approve / Reject
 * are inline; reject requires a mandatory reason captured in a dialog.
 */
export default function EarlyDeliveryApprovalsPanel({ onCountChange }) {
  const navigate = useNavigate();
  const { activeUser, activeLocationIds, locations } = useApp();
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, filtered: 0 });
  const [loading, setLoading] = useState(true);
  const [actionFor, setActionFor] = useState(null);  // { mode: 'approve' | 'reject', sale }
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const canApprove = activeUser?.role === "Accounts" || activeUser?.role === "Admin";

  const locMap = Object.fromEntries(locations.map((l) => [l.id, l]));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: tab };
      if (activeLocationIds?.length) {
        params.location_ids = activeLocationIds.join(",");
      }
      const data = await EarlyDelivery.queue(params);
      setItems(data?.items || []);
      setCounts({
        pending: data?.pending_count || 0,
        filtered: data?.filtered_pending || 0,
      });
      onCountChange?.(data?.pending_count || 0);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [tab, activeLocationIds, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (row.sr_number || "").toLowerCase().includes(q) ||
      (row.customer?.name || "").toLowerCase().includes(q) ||
      (row.customer?.phone || "").includes(q) ||
      (row.vehicle?.model || "").toLowerCase().includes(q)
    );
  });

  const doApprove = async () => {
    if (!actionFor?.sale) return;
    setBusy(true);
    try {
      await EarlyDelivery.approve(actionFor.sale.sale_id, {});
      toast.success("Early delivery approved");
      setActionFor(null);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed");
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!actionFor?.sale) return;
    if (!rejectReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    setBusy(true);
    try {
      await EarlyDelivery.reject(actionFor.sale.sale_id, {
        rejection_reason: rejectReason.trim(),
      });
      toast.success("Early delivery rejected");
      setActionFor(null);
      setRejectReason("");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed");
    } finally { setBusy(false); }
  };

  const approvedCount = items.filter((i) => i.early_delivery?.status === "approved").length;
  const rejectedCount = items.filter((i) => i.early_delivery?.status === "rejected").length;

  return (
    <div className="space-y-6" data-testid="early-delivery-panel">
      {/* Page intro */}
      <div>
        <div className={labelCls}>Accounts · Approvals</div>
        <h1 className="mt-1 font-display text-3xl font-bold text-onedesk-textPrimary tracking-tight">
          Early Delivery Approvals
        </h1>
        <p className="mt-1 text-sm text-onedesk-textSecondary">
          Approve or reject Sales-team requests to deliver vehicles before all workflow stages complete.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Pending Approval"
          value={counts.pending}
          Icon={AlertOctagon}
          helper="Requires action"
          testId="ed-stat-pending"
        />
        <StatCard
          label="Approved (this view)"
          value={approvedCount}
          Icon={CheckCircle2}
          helper="Visible in current tab"
          testId="ed-stat-approved"
        />
        <StatCard
          label="Rejected (this view)"
          value={rejectedCount}
          Icon={XCircle}
          helper="Visible in current tab"
          testId="ed-stat-rejected"
        />
        <StatCard
          label="In View"
          value={filtered.length}
          Icon={ListTodo}
          helper={tab === "all" ? "All time" : `${tab} requests`}
          testId="ed-stat-in-view"
        />
      </div>

      {/* Filters */}
      <div className={`${cardCls} p-4`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-onedesk-textSecondary" />
            <Input
              placeholder="Search SR, customer, phone or model"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="ed-search"
            />
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              {STATUS_TABS.map((t) => (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  data-testid={`ed-tab-${t.value}`}
                >
                  {t.label}
                  {t.value === "pending" && counts.pending > 0 && (
                    <span className="ml-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-onedesk-warning px-1 text-[10px] font-bold text-white">
                      {counts.pending}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Queue list */}
      {loading ? (
        <div className={`${cardCls} p-10 text-center text-sm text-onedesk-textSecondary`}>
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Nothing to action"
          description={
            tab === "pending"
              ? "No Early Delivery requests are awaiting your approval. Excellent."
              : `No ${tab} requests in this view.`
          }
          Icon={CheckCircle2}
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => (
            <ApprovalRow
              key={row.sale_id}
              row={row}
              loc={locMap[row.location_id]}
              canApprove={canApprove}
              onView={() => navigate(`/sales/${row.sale_id}`)}
              onApprove={() => setActionFor({ mode: "approve", sale: row })}
              onReject={() => setActionFor({ mode: "reject", sale: row })}
            />
          ))}
        </div>
      )}

      {/* Approve confirmation dialog */}
      <Dialog
        open={actionFor?.mode === "approve"}
        onOpenChange={(o) => !o && setActionFor(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-onedesk-success" />
              Approve Early Delivery?
            </DialogTitle>
            <DialogDescription>
              Sales team will be able to mark this vehicle as delivered immediately,
              even though some workflow stages are still pending.
            </DialogDescription>
          </DialogHeader>
          {actionFor?.sale && <DialogSummary row={actionFor.sale} />}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setActionFor(null)}
              data-testid="ed-approve-cancel"
            >Cancel</Button>
            <Button
              onClick={doApprove}
              disabled={busy}
              className="bg-onedesk-success text-white hover:opacity-90"
              data-testid="ed-approve-confirm"
            >
              {busy ? "Approving…" : "Approve Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog (reason mandatory) */}
      <Dialog
        open={actionFor?.mode === "reject"}
        onOpenChange={(o) => {
          if (busy) return;
          if (!o) { setActionFor(null); setRejectReason(""); }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-onedesk-danger" />
              Reject Early Delivery?
            </DialogTitle>
            <DialogDescription>
              Provide a clear reason. The Sales team will see it on the sale and
              can submit a fresh request afterwards.
            </DialogDescription>
          </DialogHeader>
          {actionFor?.sale && <DialogSummary row={actionFor.sale} />}
          <div className="space-y-1.5">
            <Label htmlFor="ed-reject-reason" className="text-xs font-semibold">
              Rejection reason *
            </Label>
            <Textarea
              id="ed-reject-reason"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Insurance & HSRP can be done same day — no need to skip"
              disabled={busy}
              data-testid="ed-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => { setActionFor(null); setRejectReason(""); }}
              data-testid="ed-reject-cancel"
            >Cancel</Button>
            <Button
              onClick={doReject}
              disabled={busy || !rejectReason.trim()}
              className="bg-onedesk-danger text-white hover:opacity-90 disabled:bg-onedesk-border"
              data-testid="ed-reject-confirm"
            >
              {busy ? "Rejecting…" : "Reject Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────── Row ───────────
function ApprovalRow({ row, loc, canApprove, onView, onApprove, onReject }) {
  const ed = row.early_delivery || {};
  const cur = ed.current_request || {};
  const status = ed.status;  // pending | approved | rejected
  const pendingItems = cur.pending_items || [];

  const statusMeta = {
    pending:  { label: "Pending",  cls: "bg-onedesk-warning/10 text-onedesk-warning border-onedesk-warning/30" },
    approved: { label: "Approved", cls: "bg-onedesk-success/10 text-onedesk-success border-onedesk-success/30" },
    rejected: { label: "Rejected", cls: "bg-onedesk-danger/10 text-onedesk-danger border-onedesk-danger/30" },
  }[status] || { label: status, cls: "" };

  return (
    <div
      className={`${cardCls} p-5 transition-shadow hover:shadow-md`}
      data-testid={`ed-row-${row.sale_id}`}
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        {/* Identity */}
        <div className="lg:col-span-3">
          <div className="font-mono-data text-xs font-bold text-onedesk-textSecondary">
            {row.sr_number || "—"}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <User className="h-3.5 w-3.5 text-onedesk-textSecondary" />
            <span className="text-sm font-semibold text-onedesk-textPrimary">
              {row.customer?.name || "—"}
            </span>
          </div>
          <div className="mt-0.5 ml-5 font-mono-data text-xs text-onedesk-textSecondary">
            {row.customer?.phone || "—"}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Bike className="h-3.5 w-3.5 text-onedesk-textSecondary" />
            <span className="text-sm text-onedesk-textPrimary">
              {row.vehicle?.model || "—"}
              {row.vehicle?.color ? <span className="text-onedesk-textSecondary"> · {row.vehicle.color}</span> : null}
            </span>
          </div>
          {loc && (
            <div className="mt-1 ml-5 text-[11px] text-onedesk-textSecondary">
              {loc.name} ({loc.code})
            </div>
          )}
        </div>

        {/* Requested + Pending items */}
        <div className="lg:col-span-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-od-input border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${statusMeta.cls}`}>
              {statusMeta.label}
            </span>
            {cur.requested_by_name && (
              <span className="text-xs text-onedesk-textSecondary">
                by <span className="font-semibold text-onedesk-textPrimary">{cur.requested_by_name}</span>
                <span className="opacity-80"> ({cur.requested_by_role})</span>
              </span>
            )}
            {cur.requested_at && (
              <span className="inline-flex items-center gap-1 text-[11px] text-onedesk-textSecondary">
                <Clock className="h-3 w-3" />
                <span className="font-mono-data">{fmtDateTime(cur.requested_at)}</span>
              </span>
            )}
          </div>
          {cur.reason && (
            <div className="mt-2 flex items-start gap-1.5 rounded-od-input border border-onedesk-border bg-onedesk-bg px-3 py-2">
              <MessageSquare className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-onedesk-textSecondary" />
              <p className="text-xs text-onedesk-textPrimary">{cur.reason}</p>
            </div>
          )}
          {status === "rejected" && cur.rejection_reason && (
            <div className="mt-2 rounded-od-input border border-onedesk-danger/30 bg-onedesk-danger/5 px-3 py-2 text-xs text-onedesk-danger">
              <span className="font-semibold">Rejection:</span> {cur.rejection_reason}
              {cur.decided_by_name && (
                <span className="opacity-80"> · by {cur.decided_by_name}</span>
              )}
            </div>
          )}
          {pendingItems.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-onedesk-textSecondary">
                Pending Items
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {pendingItems.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center rounded-full bg-onedesk-bg px-2 py-0.5 text-[11px] font-semibold text-onedesk-textPrimary ring-1 ring-onedesk-border"
                  >
                    {TASK_TYPE_LABELS[t] || t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="lg:col-span-4">
          <div className="flex flex-wrap items-center justify-start gap-2 lg:justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={onView}
              className="gap-1.5"
              data-testid={`ed-view-${row.sale_id}`}
            >
              <Eye className="h-3.5 w-3.5" /> View Sale
            </Button>
            {status === "pending" && canApprove && (
              <>
                <Button
                  size="sm"
                  onClick={onApprove}
                  className="gap-1.5 bg-onedesk-success text-white hover:opacity-90"
                  data-testid={`ed-approve-${row.sale_id}`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onReject}
                  className="gap-1.5 border-onedesk-danger/30 text-onedesk-danger hover:bg-onedesk-danger/5"
                  data-testid={`ed-reject-${row.sale_id}`}
                >
                  <XCircle className="h-3.5 w-3.5" /> Reject
                </Button>
              </>
            )}
          </div>
          {row.sale_status === "delivered" && ed.delivery_type === "conditional" && (
            <div className="mt-2 text-right text-[11px] text-onedesk-success">
              <CheckCircle2 className="mr-1 inline h-3 w-3" />
              Delivered conditionally · {(ed.pending_items_at_delivery || []).length} items pending at delivery
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DialogSummary({ row }) {
  const cur = row.early_delivery?.current_request || {};
  return (
    <div className="rounded-od-input border border-onedesk-border bg-onedesk-bg p-3 text-sm">
      <div className="flex items-center justify-between">
        <Link
          to={`/sales/${row.sale_id}`}
          className="font-mono-data text-xs font-bold text-onedesk-primary hover:underline"
        >
          {row.sr_number}
        </Link>
        <span className="text-xs text-onedesk-textSecondary">
          {row.customer?.name} · {row.vehicle?.model}
        </span>
      </div>
      {cur.reason && (
        <div className="mt-2 text-xs text-onedesk-textPrimary">
          <span className="font-semibold">Reason:</span> {cur.reason}
        </div>
      )}
      {(cur.pending_items || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {(cur.pending_items || []).map((t) => (
            <span
              key={t}
              className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-onedesk-textPrimary ring-1 ring-onedesk-border"
            >
              {TASK_TYPE_LABELS[t] || t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
